/**
 * Модель страницы /expertise/[category]/ — загрузка публикаций категории.
 *
 * Единственная задача файла: дать вызывающему коду простой метод `getPosts`,
 * который делает запрос через `useBackendAPI`, приводит ответ к удобному виду
 * и возвращает данные. Раскладку по карточкам, скролл, обновление URL делает страница.
 */

import { useBackendAPI } from '@nuxt-shared/composables/useBackendAPI';
import { usePublicationDateText } from '@nuxt-shared/composables';
import type { CategoryFeedPost } from './ui';

/** API Endpoint бэкенда со списком публикаций категории. */
const PUBLICATION_ENDPOINT = 'expertise/category/publication/';

/** Отдельный API Endpoint для сводной категории `/expertise/all`. */
const PUBLICATION_ALL_ENDPOINT = 'expertise/category/publication/all/';

/**
 * Слаг сводной категории, у которой свой эндпоинт и формат параметров.
 * Экспортируется: страница исключает её из проверки на несуществующую категорию —
 * заголовка у сводной ленты нет by design.
 */
export const ALL_CATEGORY = 'all';

/** Параметры запроса публикаций категории. */
export interface InsertPostsParams {
  /** Путь категории, например `/expertise/webinars/`. */
  moreLink: string;
  /** Номер страницы (пагинация). */
  p?: number;
  /** Поисковый запрос. */
  q?: string;
  /** Выбранные теги фильтра. */
  queryTags?: string[];
  /** Флаг сброса кэша на бэкенде (`'Y'` | `'N'` | null). */
  clearCache?: string | null;
}

/** Нормализованные данные ленты категории, готовые для использования на странице. */
export interface CategoryFeedData {
  title: string;
  description: string;
  page: { count: number; size: number };
  filter: { QUERY: string; QUERY_TAGS: string[] };
  tags: {
    list: string[];
    clickable: boolean;
    path: string;
    quantity: Record<string, number>;
  };
  posts: CategoryFeedPost[];
}

/**
 * Форматирует дату публикации.
 *
 * Тип публикации определяем по её `LINK`, а не по слагу страницы: в сводной
 * категории `/expertise/all` элементы разных типов смешаны. Для вебинаров берём
 * `PROPERTIES.DATE_START.VALUE` и формат события, для остального — `ACTIVE_FROM`.
 */
const formatDate = (item: any): string => {
  const isWebinar = (item?.['LINK'] || '').includes('/webinars/');
  const rawDate = isWebinar
    ? item?.['PROPERTIES']?.['DATE_START']?.['VALUE']
    : item?.['ACTIVE_FROM'];

  if (!rawDate) return '';

  return usePublicationDateText(rawDate, {
    isWebinar,
    dateEnd: item?.['PROPERTIES']?.['DATE_END']?.['VALUE'] || 0,
  });
};

/**
 * Загружает публикации категории через backend API.
 *
 * Возвращает нормализованные данные — что и куда вставлять решает вызывающий код.
 * Это упрощённый аналог `insertCards` из легаси (`category-feed/src/app.js`):
 * без `reloadCards` (страница на SSR) и без отдельного метода `getExpertise`.
 *
 * @param params Параметры запроса (`moreLink`, `p`, `q`, `queryTags`, `clearCache`).
 * @param category Слаг категории из роута — определяет эндпоинт и формат параметров.
 * @param fetchConfig Доп. опции `useFetch` — например `key` и `headers` для SSR-загрузки.
 * @returns Нормализованные данные или `null`, если бэкенд ничего не вернул.
 */
export async function getPosts(
  { p = 1, q = '', queryTags = [], clearCache = null }: InsertPostsParams,
  category: string,
  fetchConfig: Record<string, any> = {},
): Promise<CategoryFeedData | null> {
  const isAll = category === ALL_CATEGORY;

  // У сводной категории `/expertise/all` свой эндпоинт и свой формат параметров:
  // `queryTags` уходит массивом (`queryTags=tag1&queryTags=tag2`), без `moreLink`/`clearCache`.
  // У обычной категории бэк ждёт один параметр `queryTags=tag1,tag2` — поэтому джойним.
  const endpoint = isAll ? PUBLICATION_ALL_ENDPOINT : PUBLICATION_ENDPOINT;
  const query = isAll
    ? { p, q, queryTags }
    : {
        moreLink: `/expertise/${category}/`,
        p,
        q,
        queryTags: queryTags.join(','),
        clearCache,
      };

  const { data } = await useBackendAPI(endpoint, {
    query,
    // ВАЖНО: переданный сюда `transform` перекрывает обёрточный в useBackendAPI
    // (он спредится из fetchConfig последним) и получает ПОЛНЫЙ ответ
    // `{ status, data, errors }`. useBackendAPI затем достаёт из результата `.data`,
    // поэтому возвращаем конверт целиком, подменив только поле `data`.
    transform: (res: any) => {
      const payload = res?.data;
      const items = payload?.ITEMS;

      const normalized: CategoryFeedData | null = items
        ? {
            title: payload['TITLE'] || '',
            description: payload['DESCRIPTION'] || '',
            page: {
              count: payload['COUNT_PAGE'] || 0,
              size: payload['PAGE_SIZE'] || 0,
            },
            filter: payload['FILTER'] || { QUERY: '', QUERY_TAGS: [] },
            tags: {
              list: [...(payload['TAGS'] || [])],
              clickable: Boolean(payload['TAGS_CLICKABLE']),
              path: payload['TAGS_PATH'] || '',
              quantity: payload['TAGS_QUANTITY'] || {},
            },
            posts: items.map((item: any) => ({
              id: item['ID'],
              title: item['NAME'],
              description: item?.['PREVIEW_TEXT_SHORT'] || '',
              date: formatDate(item),
              pictures: item['IMAGE'] || {},
              group: '',
              tags: item['TAGS'] || [],
              url: item['LINK'],
              inactive: false,
              show: true,
            })),
          }
        : null;

      return { ...res, data: normalized };
    },
    ...fetchConfig,
  });

  return data.value || null;
}
